import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { DeductibleConceptDTO, DeductibleConceptRequestDTO } from '../../dto/DeductibleConceptDTO';
import {
    createDeductibleConcept,
    getDeductibleConcepts,
    updateDeductibleConcept,
} from '../../services/DeductibleConceptController';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import LoadTable, { LoadTableColumn, LoadTableRow } from '../../components/novarium/LoadTable';
import NvTooltips from '../../components/novarium/NvTooltips';
import { AddRowButton, FieldError } from '../../components/novarium/NvTableParts';

/** Largo de la columna de la descripción. */
const DESCRIPTION_MAX_LENGTH = 100;

type FieldName = 'description' | 'percentage';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Campo del request → campo del formulario, para los marcadores de VALIDATION_ERROR y del duplicado. */
const REQUEST_FIELDS: Record<string, FieldName> = { description: 'description', percentage: 'percentage' };

type Row = LoadTableRow & { concept: DeductibleConceptDTO };

const rowKeyOf = (concept: DeductibleConceptDTO) => `c-${concept.deductibleConcept}`;

function formatPercentage(percentage: number): string {
    return `${percentage.toLocaleString('es-AR', { maximumFractionDigits: 4 })}%`;
}

/**
 * Conceptos deducibles (ej. "Caja de profesionales", 5 %): porcentajes que la liquidación de honorarios descuenta de
 * los honorarios de los profesionales que los tienen asignados. Input table de Novarium: alta y edición en la primera
 * fila, cada acción se guarda en el momento. No se eliminan, porque los usan las liquidaciones cerradas: se desactivan.
 */
export default function DeductibleConceptView() {
    const notify = useNotify();

    const [concepts, setConcepts] = useState<DeductibleConceptDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [description, setDescription] = useState('');
    const [percentage, setPercentage] = useState<number | null>(null);
    const [active, setActive] = useState(true);
    const [editing, setEditing] = useState<DeductibleConceptDTO | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const descriptionRef = useRef<HTMLInputElement>(null);
    const percentageRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setConcepts(await getDeductibleConcepts());
        } catch {
            notify.error('No se pudieron cargar los conceptos deducibles. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    function resetForm() {
        setDescription('');
        setPercentage(null);
        setActive(true);
        setEditing(null);
        setErrors({});
    }

    function startEdit(concept: DeductibleConceptDTO) {
        setDescription(concept.description);
        setPercentage(concept.percentage);
        setActive(concept.active);
        setEditing(concept);
        setErrors({});
        descriptionRef.current?.focus();
    }

    function cancelEdit() {
        resetForm();
        descriptionRef.current?.focus();
    }

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (!description.trim()) result.description = 'Completá la descripción';
        if (percentage == null || percentage <= 0) result.percentage = 'Ingresá un porcentaje mayor a 0';
        else if (percentage > 100) result.percentage = 'Ingresá un porcentaje de hasta 100';
        return result;
    }

    function focusFirstError(fieldErrors: FieldErrors) {
        if (fieldErrors.description) descriptionRef.current?.focus();
        else if (fieldErrors.percentage) percentageRef.current?.focus();
    }

    async function handleSubmit() {
        if (saving) return;
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }

        const body: DeductibleConceptRequestDTO = { description: description.trim(), percentage: percentage!, active };
        setSaving(true);
        try {
            if (editing) {
                const updated = await updateDeductibleConcept(editing.deductibleConcept, body);
                setConcepts(current => current.map(c => c.deductibleConcept === updated.deductibleConcept ? updated : c));
                notify.success('Cambios guardados');
            } else {
                const created = await createDeductibleConcept(body);
                // La fila nueva va primera
                setConcepts(current => [created, ...current]);
                notify.success('Concepto deducible creado');
            }
            resetForm();
            descriptionRef.current?.focus();
        } catch (error: unknown) {
            handleSaveError(error);
        } finally {
            setSaving(false);
        }
    }

    function handleSaveError(error: unknown) {
        const apiError = parseApiError(error);
        const fieldErrors: FieldErrors = {};
        for (const detail of apiError.details ?? []) {
            const field = REQUEST_FIELDS[detail.field];
            if (field) fieldErrors[field] = detail.message;
        }
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            focusFirstError(fieldErrors);
            return;
        }
        if (apiError.status === 404) {
            // Ya no está (otra sede u otra pestaña): se recarga la lista
            notify.error(apiError.message);
            resetForm();
            load();
            return;
        }
        const isBusinessError = apiError.status >= 400 && apiError.status < 500;
        const fallback = editing
            ? 'No se pudieron guardar los cambios. Intentá de nuevo.'
            : 'No se pudo crear el concepto deducible. Intentá de nuevo.';
        notify.error(isBusinessError ? apiError.message : fallback);
    }

    /** Enter guarda; Esc descarta la edición. */
    function handleFormKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        } else if (event.key === 'Escape' && editing) {
            event.preventDefault();
            cancelEdit();
        }
    }

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'description',
            header: 'Descripción',
            className: 'nv-col-item',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <InputText
                        id="deductible-concept-description"
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setErrors(current => ({ ...current, description: undefined }));
                        }}
                        onBlur={() => {
                            if (!description.trim() && (percentage != null || editing)) {
                                setErrors(current => ({ ...current, description: 'Completá la descripción' }));
                            }
                        }}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder="Ej.: Caja de profesionales"
                        className={classNames('nv-in-cell', { 'p-invalid': errors.description, 'p-filled': description })}
                        aria-label="Descripción"
                        aria-invalid={errors.description ? true : undefined}
                        aria-describedby={errors.description ? 'deductible-concept-description-error' : undefined}
                    />
                    <FieldError id="deductible-concept-description-error" message={errors.description} />
                </div>
            ),
            body: ({ concept }) => concept.description,
        },
        {
            key: 'percentage',
            header: 'Porcentaje',
            align: 'right',
            className: 'nv-col-pct',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <InputNumber
                        inputId="deductible-concept-percentage"
                        inputRef={percentageRef}
                        value={percentage}
                        onValueChange={(e) => {
                            setPercentage(e.value ?? null);
                            setErrors(current => ({ ...current, percentage: undefined }));
                        }}
                        mode="decimal"
                        locale="es-AR"
                        minFractionDigits={0}
                        maxFractionDigits={4}
                        suffix="%"
                        min={0}
                        max={100}
                        placeholder="Ej.: 5%"
                        className={classNames('nv-number nv-in-cell', { 'p-invalid': errors.percentage })}
                        inputClassName={classNames({ 'p-filled': percentage != null })}
                        aria-label="Porcentaje"
                        aria-invalid={errors.percentage ? true : undefined}
                        aria-describedby={errors.percentage ? 'deductible-concept-percentage-error' : undefined}
                    />
                    <FieldError id="deductible-concept-percentage-error" message={errors.percentage} />
                </div>
            ),
            body: ({ concept }) => formatPercentage(concept.percentage),
        },
        {
            key: 'active',
            header: 'Activo',
            align: 'center',
            className: 'nv-col-bool',
            addCell: (
                <InputSwitch checked={active} onChange={(e) => setActive(!!e.value)} aria-label="Activo" />
            ),
            body: ({ concept }) => (concept.active ? 'Sí' : 'No'),
        },
    ];

    const addAction = editing ? (
        <span className="nv-cell-actions">
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-check"
                aria-label="Confirmar cambios"
                data-pr-tooltip="Confirmar cambios"
                loading={saving}
                onClick={handleSubmit}
            />
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-times"
                aria-label="Cancelar cambios"
                data-pr-tooltip="Cancelar cambios"
                disabled={saving}
                onClick={cancelEdit}
            />
        </span>
    ) : (
        <AddRowButton label="Agregar" loading={saving} onClick={handleSubmit} />
    );

    const rows: Row[] = concepts.map(concept => ({ rowKey: rowKeyOf(concept), concept }));

    return (
        <div className="nv-scope nv-page">
            <NvTooltips />
            <div className="nv-page-head">
                <h1 className="nv-h1">Conceptos deducibles</h1>
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    Se descuentan de los honorarios de cada liquidación, sin restar nada administrativo, a los profesionales
                    que los tienen asignados en Profesionales › Deducciones.
                </p>
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    No se pueden eliminar porque los usan las liquidaciones cerradas: desactivalos para que dejen de aplicarse.
                </p>
            </div>

            {loading ? (
                <div className="nv-loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                    <span>Cargando…</span>
                </div>
            ) : (
                <LoadTable
                    rows={rows}
                    columns={columns}
                    addAction={addAction}
                    rowActions={({ concept }) => (
                        <span className="nv-cell-actions">
                            <Button
                                type="button"
                                text
                                className="nv-icon nv-tip"
                                icon="pi pi-pencil"
                                aria-label={`Editar ${concept.description}`}
                                data-pr-tooltip="Editar"
                                disabled={saving}
                                onClick={() => startEdit(concept)}
                            />
                        </span>
                    )}
                    emptyMessage="Todavía no hay conceptos deducibles cargados."
                    editingRowKey={editing ? rowKeyOf(editing) : null}
                />
            )}
        </div>
    );
}
